import { supabase } from './supabase';

export const inviteService = {
  /**
   * Send an invite to a user by email.
   * Creates an invite record and optionally triggers Supabase invite email.
   *
   * @param {Object} params
   * @param {string} params.email - Email to invite
   * @param {string} params.defaultRole - Role to assign ('user' or 'admin')
   * @param {Array} params.competitionAccess - Array of {competition_id, role} for pre-granted access
   */
  async sendInvite({ email, defaultRole = 'user', competitionAccess = [] }) {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) throw new Error('Must be logged in to send invites');

    // Create invite record
    const { data: invite, error: createError } = await supabase
      .from('user_invites')
      .insert({
        email: email.toLowerCase().trim(),
        invited_by: user.id,
        default_role: defaultRole,
        competition_access: competitionAccess
      })
      .select()
      .single();

    if (createError) {
      if (createError.code === '23505') {
        throw new Error('An invite for this email already exists');
      }
      throw createError;
    }

    // Log the invite
    await supabase.rpc('log_audit_event', {
      p_action: 'invite.sent',
      p_resource_type: 'invite',
      p_resource_id: invite.id,
      p_new_value: {
        email,
        default_role: defaultRole,
        competition_count: competitionAccess.length
      }
    });

    return invite;
  },

  /**
   * Get list of invites with optional filtering
   */
  async getInvites({ status, limit = 50, offset = 0 } = {}) {
    let query = supabase
      .from('user_invites')
      .select(`
        *,
        inviter:invited_by (
          email
        )
      `, { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data, count };
  },

  /**
   * Revoke a pending invite
   */
  async revokeInvite(inviteId) {
    const { data: invite, error: fetchError } = await supabase
      .from('user_invites')
      .select('email, status')
      .eq('id', inviteId)
      .single();

    if (fetchError) throw fetchError;
    if (invite.status !== 'pending') {
      throw new Error('Can only revoke pending invites');
    }

    const { error } = await supabase
      .from('user_invites')
      .update({ status: 'revoked' })
      .eq('id', inviteId);

    if (error) throw error;

    await supabase.rpc('log_audit_event', {
      p_action: 'invite.revoked',
      p_resource_type: 'invite',
      p_resource_id: inviteId,
      p_old_value: { status: 'pending' },
      p_new_value: { status: 'revoked', email: invite.email }
    });
  },

  /**
   * Resend an invite (extends expiration)
   */
  async resendInvite(inviteId) {
    const newExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    const { data: invite, error } = await supabase
      .from('user_invites')
      .update({
        expires_at: newExpiry,
        status: 'pending'  // In case it was expired
      })
      .eq('id', inviteId)
      .select()
      .single();

    if (error) throw error;

    await supabase.rpc('log_audit_event', {
      p_action: 'invite.resent',
      p_resource_type: 'invite',
      p_resource_id: inviteId,
      p_new_value: { email: invite.email, expires_at: newExpiry }
    });

    return invite;
  },

  /**
   * Validate an invite token (used during signup)
   */
  async validateInviteToken(token) {
    const { data, error } = await supabase
      .from('user_invites')
      .select('*')
      .eq('token', token)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error) return null;
    return data;
  },

  /**
   * Accept an invite (called after user signs up)
   */
  async acceptInvite(token) {
    const { data, error } = await supabase.rpc('accept_invite', { p_token: token });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);

    return data;
  },

  /**
   * Get invite statistics
   */
  async getInviteStats() {
    const { data, error } = await supabase
      .from('user_invites')
      .select('status');

    if (error) throw error;

    const stats = {
      pending: 0,
      accepted: 0,
      expired: 0,
      revoked: 0,
      total: data.length
    };

    data.forEach(invite => {
      stats[invite.status] = (stats[invite.status] || 0) + 1;
    });

    return stats;
  }
};
